/* global BigInt */
'use strict';

// Identical pure calculation module is used by the frontend. Dimensions are cm.
const FIELDS = ['weight_kg', 'volume_cbm'];
const isMissing = value => value == null || (typeof value === 'string' && value.trim() === '')
  || ((typeof value === 'string' || typeof value === 'number') && Number.isFinite(Number(value)) && Number(value) === 0);
const positive = value => (typeof value === 'string' || typeof value === 'number')
  && String(value).trim() !== '' && Number.isFinite(Number(value)) && Number(value) > 0;

function dimensionMilli(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim().replace(/^\./, '0.');
  const match = /^(\d+)(?:\.(\d{1,3}))?$/.exec(text);
  if (!match) return null;
  const scaled = BigInt(match[1]) * 1000n + BigInt((match[2] || '').padEnd(3, '0'));
  return scaled > 0n && scaled <= 999999999999n ? scaled : null;
}
function fixed(value, places) {
  const text = value.toString().padStart(places + 1, '0');
  return `${text.slice(0, -places)}.${text.slice(-places)}`;
}
function calculate(values = {}) {
  const dimensions = ['length_cm', 'width_cm', 'height_cm'].map(key => dimensionMilli(values[key]));
  if (dimensions.some(value => value === null)) return { values: {}, reason: '가로·세로·높이를 cm 단위의 양수로 입력하세요. 소수점은 3자리까지 가능합니다.' };
  const product = dimensions.reduce((result, value) => result * value, 1n);
  const rounded = divisor => (product + divisor / 2n) / divisor;
  const air = rounded(6000000000n); // L*W*H / 6000, rounded to 0.001 kg
  const cbm = rounded(1000000000n); // L*W*H / 1e6, rounded to 0.000001 m3
  const valuesOut = {};
  // Match the existing packing save contract; never suggest rounded zero or overflow.
  if (air > 0n && air <= 9999990n) valuesOut.weight_kg = fixed(air, 3);
  if (cbm > 0n && cbm <= 9999990000n) valuesOut.volume_cbm = fixed(cbm, 6);
  return { values: valuesOut, reason: '반올림 결과가 0이거나 저장 범위를 벗어난 필드는 제안하지 않습니다.' };
}
function suggestions(values = {}) {
  const result = calculate(values);
  return { ...result, values: Object.fromEntries(FIELDS.filter(key => isMissing(values[key]) && result.values[key] !== undefined)
    .map(key => [key, result.values[key]])) };
}
function acceptedPayload(values = {}) {
  // A dimension edit must not turn an old calculation into an unguarded manual value.
  const fields = Array.isArray(values.dimension_default_fields)
    ? values.dimension_default_fields.filter(key => FIELDS.includes(key)) : [];
  return fields.length ? { dimension_default_fields: [...new Set(fields)] } : {};
}
function clearAcceptance(values = {}, key) {
  if (!FIELDS.includes(key) || !Array.isArray(values.dimension_default_fields)) return {};
  return { dimension_default_fields: values.dimension_default_fields.filter(field => field !== key) };
}
function staleAccepted(values = {}) {
  const calculated = calculate(values).values;
  return (acceptedPayload(values).dimension_default_fields || []).filter(key =>
    calculated[key] === undefined || !positive(values[key]) || Number(values[key]) !== Number(calculated[key]));
}
function applySuggestions(values = {}) {
  const patch = suggestions(values).values;
  const fields = [...new Set([...(acceptedPayload(values).dimension_default_fields || []), ...Object.keys(patch)])];
  return fields.length ? { ...patch, dimension_default_fields: fields } : patch;
}
function resolveAccepted(values = {}, current = null) {
  const fields = values.dimension_default_fields;
  if (fields === undefined) return values;
  if (!Array.isArray(fields) || fields.some(key => !FIELDS.includes(key)) || new Set(fields).size !== fields.length) {
    throw Object.assign(new Error('계산값 적용 항목을 확인하세요.'), { status: 400, code: 'VALIDATION' });
  }
  const calculated = calculate(values).values;
  const resolved = { ...values };
  for (const key of fields) {
    if (calculated[key] === undefined || (!isMissing(values[key]) && Number(values[key]) !== Number(calculated[key]))) {
      throw Object.assign(new Error('치수가 변경되었거나 계산값이 유효하지 않습니다. 계산값을 다시 확인하세요.'), { status: 400, code: 'VALIDATION' });
    }
    if (current && !isMissing(current[key])) {
      if (!positive(current[key])) throw Object.assign(new Error('기존 물류값을 확인하세요.'), { status: 409, code: 'VALIDATION' });
      resolved[key] = current[key]; // A concurrently entered positive value always wins over a suggestion.
    } else resolved[key] = calculated[key];
  }
  delete resolved.dimension_default_fields;
  return resolved;
}
module.exports = { FIELDS, isMissing, positive, calculate, suggestions, acceptedPayload, clearAcceptance, staleAccepted, applySuggestions, resolveAccepted };
